import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin'
import {
  EmptyState,
  LoadingState,
  TypeaheadAvatar,
  TypeaheadDropdownContainer,
  TypeaheadDropdownHeader,
  TypeaheadInfo,
  TypeaheadOption,
  TypeaheadPrimaryText,
  TypeaheadScrollArea,
  TypeaheadSecondaryText,
} from 'features/richText/components/TypeaheadDropdownStyles'
import { createMentionNode } from 'features/richText/nodes/MentionNode'
import { $createTextNode, TextNode } from 'lexical'
import React, { useCallback, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { TaggableUser } from 'types'

const TRIGGER_CHAR = '@'

class MentionTypeaheadOption extends MenuOption {
  user: TaggableUser
  constructor(user: TaggableUser) {
    super(user.name || user.email)
    this.user = user
  }
}

export interface MentionPluginBaseProps {
  users: TaggableUser[]
  isLoading: boolean
  maxSuggestions?: number
}

export const MentionPluginBase: React.FC<MentionPluginBaseProps> = ({
  users,
  isLoading,
  maxSuggestions = 10,
}) => {
  const [queryString, setQueryString] = useState<string | null>(null)

  const options = useMemo(() => {
    if (!queryString) return []
    const q = queryString.toLowerCase().trim()
    return users
      .filter((user) => {
        const name = user.name?.toLowerCase() ?? ''
        const email = user.email?.toLowerCase() ?? ''
        return name.includes(q) || email.includes(q)
      })
      .sort((a, b) => {
        const aName = a.name?.toLowerCase() ?? ''
        const bName = b.name?.toLowerCase() ?? ''
        const aStarts = aName.startsWith(q)
        const bStarts = bName.startsWith(q)
        if (aStarts && !bStarts) return -1
        if (!aStarts && bStarts) return 1
        return aName.localeCompare(bName)
      })
      .slice(0, maxSuggestions)
      .map((user) => new MentionTypeaheadOption(user))
  }, [queryString, users, maxSuggestions])

  const onSelectOption = useCallback(
    (
      selectedOption: MentionTypeaheadOption,
      nodeToReplace: TextNode | null,
      closeMenu: () => void
    ) => {
      const mentionNode = createMentionNode(selectedOption.user)
      if (nodeToReplace) {
        nodeToReplace.replace(mentionNode)
      }
      mentionNode.insertAfter($createTextNode(' '))
      closeMenu()
    },
    []
  )

  const checkForMentionMatch = useBasicTypeaheadTriggerMatch(TRIGGER_CHAR, {
    minLength: 0,
    allowWhitespace: true,
  })

  return (
    <LexicalTypeaheadMenuPlugin<MentionTypeaheadOption>
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      triggerFn={checkForMentionMatch}
      options={options}
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }
      ) => {
        if (!anchorElementRef.current) return null
        return createPortal(
          <TypeaheadDropdownContainer $minWidth='16rem' $maxWidth='20rem'>
            <TypeaheadDropdownHeader>Mention User</TypeaheadDropdownHeader>
            <TypeaheadScrollArea>
              {isLoading ? (
                <LoadingState>Loading users...</LoadingState>
              ) : options.length === 0 ? (
                <EmptyState>No users found</EmptyState>
              ) : (
                options.map((option, index) => (
                  <TypeaheadOption
                    key={option.user.id}
                    ref={option.setRefElement}
                    $isSelected={selectedIndex === index}
                    onClick={() => {
                      setHighlightedIndex(index)
                      selectOptionAndCleanUp(option)
                    }}
                    onMouseEnter={() => setHighlightedIndex(index)}>
                    <TypeaheadAvatar $shape='circle'>
                      {(option.user.name ||
                        option.user.email)[0]?.toUpperCase()}
                    </TypeaheadAvatar>
                    <TypeaheadInfo>
                      <TypeaheadPrimaryText>
                        {option.user.name || option.user.email}
                      </TypeaheadPrimaryText>
                      {option.user.email && option.user.name && (
                        <TypeaheadSecondaryText>
                          {option.user.email}
                        </TypeaheadSecondaryText>
                      )}
                    </TypeaheadInfo>
                  </TypeaheadOption>
                ))
              )}
            </TypeaheadScrollArea>
          </TypeaheadDropdownContainer>,
          anchorElementRef.current
        )
      }}
    />
  )
}
